import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Heart, GitCompareArrows, X, Check, Minus, TicketPercent, Copy, Compass } from "lucide-react";
import { toast } from "sonner";
import { publicApi } from "@/lib/api";
import { inr } from "@/lib/brand";
import { useCompare, useWishlist } from "@/lib/collections";
import { CarCard } from "@/components/site/CarCard";
import { RequireSignIn, PageHead } from "@/components/site/PageBits";

export function WishlistPage() {
  return <RequireSignIn title="Your wishlist" testId="wishlist-signin"><WishlistInner /></RequireSignIn>;
}

function WishlistInner() {
  const { ids } = useWishlist();
  const [cars, setCars] = useState(null);
  useEffect(() => {
    publicApi.get("/cars").then((r) => setCars(r.data)).catch(() => setCars([]));
  }, []);
  const saved = (cars || []).filter((c) => ids.includes(c.id));
  return (
    <div className="container-x py-10" data-testid="wishlist-page">
      <PageHead icon={Heart} title="Your wishlist" sub="Cars you've saved for later." />
      {cars === null ? <div className="skeleton h-40" /> : saved.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-hc-sage bg-white p-12 text-center" data-testid="wishlist-empty">
          <p className="text-hc-slate">No saved cars yet. Tap the heart on any car to save it.</p>
          <Link to="/cars" className="btn-primary mt-5">Browse cars</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">{saved.map((c, i) => <CarCard key={c.id} car={c} index={i} />)}</div>
      )}
    </div>
  );
}

const ROWS = [
  ["Price per hour", (c) => inr(c.price_per_hour), "price_per_hour", "min"],
  ["Rating", (c) => (c.rating_count ? `${c.rating_avg.toFixed(1)} ★ (${c.rating_count})` : "New"), "rating_avg", "max"],
  ["Category", (c) => c.type], ["Seats", (c) => c.seats, "seats", "max"], ["Transmission", (c) => c.transmission], ["Fuel", (c) => c.fuel],
  ["Model year", (c) => c.year, "year", "max"], ["Mileage", (c) => c.mileage || "—"], ["Boot space", (c) => c.boot || "—"],
  ["Km included / 24 hrs", (c) => `${c.km_per_day} km`, "km_per_day", "max"], ["Extra km", (c) => `₹${c.extra_km_rate}/km`, "extra_km_rate", "min"],
  ["Security deposit", (c) => inr(c.deposit), "deposit", "min"],
];

export function ComparePage() {
  const [params, setParams] = useSearchParams();
  const cmp = useCompare();
  const urlIds = params.get("ids");
  useEffect(() => {
    if (urlIds) cmp.set(urlIds.split(",").filter(Boolean));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlIds]);
  const ids = urlIds ? urlIds.split(",").filter(Boolean).slice(0, 3) : cmp.ids;
  const remove = (id) => {
    const next = ids.filter((x) => x !== id);
    cmp.set(next);
    setParams(next.length ? { ids: next.join(",") } : {}, { replace: true });
  };
  const [cars, setCars] = useState(null);
  useEffect(() => {
    publicApi.get("/cars").then((r) => setCars(r.data)).catch(() => setCars([]));
  }, []);
  const list = ids.map((id) => (cars || []).find((c) => c.id === id)).filter(Boolean);
  const features = [...new Set(list.flatMap((c) => c.features || []))];
  const best = (key, dir) => {
    const vals = list.map((c) => c[key]);
    if (new Set(vals).size < 2) return undefined;
    return dir === "min" ? Math.min(...vals) : Math.max(...vals);
  };
  return (
    <div className="container-x py-10" data-testid="compare-page">
      <PageHead icon={GitCompareArrows} title="Compare cars" sub="Side-by-side specs and prices. Best value in each row is highlighted." />
      {cars === null ? <div className="skeleton h-60" /> : list.length < 2 ? (
        <div className="rounded-2xl border border-dashed border-hc-sage bg-white p-12 text-center" data-testid="compare-empty">
          <p className="text-hc-slate">Pick at least 2 cars with “Compare” to see them side by side.</p>
          <Link to="/cars" className="btn-primary mt-5">Choose cars</Link>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-hc-sage bg-white">
          <table className="w-full min-w-[640px] text-sm" data-testid="compare-table">
            <thead>
              <tr>
                <th className="w-44 p-4" />
                {list.map((c) => (
                  <th key={c.id} className="p-4 text-left align-top" data-testid={`compare-col-${c.id}`}>
                    <div className="relative">
                      <img src={c.images.front} alt={c.name} className="aspect-[3/2] w-full rounded-xl object-cover" />
                      <button onClick={() => remove(c.id)} aria-label="Remove" className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-white shadow" data-testid={`compare-remove-${c.id}`}><X className="h-4 w-4" /></button>
                    </div>
                    <p className="mt-3 font-display text-base font-bold text-hc-ink">{c.name}</p>
                    <Link to={`/cars/${c.id}`} className="btn-primary mt-2 !px-4 !py-2 !text-xs" data-testid={`compare-book-${c.id}`}>View & book</Link>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-hc-sage">
              {ROWS.map(([label, fmt, key, dir]) => (
                <tr key={label}>
                  <td className="p-4 font-medium text-hc-slate">{label}</td>
                  {list.map((c) => <td key={c.id} className={`tabular p-4 ${key && c[key] === best(key, dir) ? "font-bold text-hc-green" : "text-hc-ink"}`}>{fmt(c)}</td>)}
                </tr>
              ))}
              {features.map((f) => (
                <tr key={f}>
                  <td className="p-4 text-hc-slate">{f}</td>
                  {list.map((c) => <td key={c.id} className="p-4">{c.features.includes(f) ? <Check className="h-4 w-4 text-hc-success" /> : <Minus className="h-4 w-4 text-hc-sage" />}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function OffersPage() {
  const [offers, setOffers] = useState(null);
  const [tiers, setTiers] = useState([]);
  useEffect(() => {
    publicApi.get("/offers").then((r) => setOffers(r.data)).catch(() => setOffers([]));
    publicApi.get("/pricing").then((r) => setTiers(r.data.duration_discounts || [])).catch(() => {});
  }, []);
  const copy = (code) => navigator.clipboard.writeText(code).then(() => toast.success(`${code} copied`)).catch(() => {});
  return (
    <div className="container-x py-10" data-testid="offers-page">
      <PageHead icon={TicketPercent} title="Offers & coupons" sub="Apply a code at checkout. Discounts are taken off before GST." />
      <div className="grid gap-4 md:grid-cols-3">
        {tiers.map((t) => (
          <div key={t.min_hours} className="rounded-2xl bg-hc-green p-6 text-white" data-testid={`offer-tier-${t.min_hours}`}>
            <p className="font-display text-3xl font-extrabold">{t.pct}% off</p>
            <p className="mt-1 text-white/80">on trips longer than {t.min_hours % 24 === 0 ? `${t.min_hours / 24} day${t.min_hours > 24 ? "s" : ""}` : `${t.min_hours} hours`} · automatic</p>
          </div>
        ))}
      </div>
      <h2 className="mt-10 font-display text-xl font-bold text-hc-ink">Coupon codes</h2>
      {offers === null ? <div className="skeleton mt-4 h-24" /> : offers.length === 0 ? (
        <p className="mt-3 rounded-2xl border border-dashed border-hc-sage bg-white p-8 text-center text-hc-slate" data-testid="offers-empty">No coupon codes are running right now. Refer a friend from My bookings and you both save ₹300.</p>
      ) : (
        <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {offers.map((o) => (
            <div key={o.code} className="card-hc flex flex-col p-6" data-testid={`offer-${o.code}`}>
              <p className="font-display text-2xl font-extrabold text-hc-green">{o.kind === "flat" ? `${inr(o.value)} off` : `${o.value}% off`}</p>
              <ul className="mt-2 flex-1 space-y-1 text-sm text-hc-slate">
                {o.kind === "percent" && o.max_discount > 0 && <li>Up to {inr(o.max_discount)}</li>}
                {o.min_order > 0 && <li>On bookings above {inr(o.min_order)}</li>}
                {o.first_trip_only && <li>First trip only</li>}
                {o.expires_on && <li>Valid till {o.expires_on.split("-").reverse().join("/")}</li>}
              </ul>
              <button onClick={() => copy(o.code)} className="mt-4 flex items-center justify-between rounded-xl border border-dashed border-hc-gold bg-hc-cream px-4 py-2.5 font-mono text-sm font-bold tracking-wider text-hc-ink" data-testid={`offer-copy-${o.code}`}>{o.code}<Copy className="h-4 w-4 text-hc-goldh" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function NotFound() {
  return (
    <div className="container-x grid min-h-[60vh] place-items-center py-16 text-center" data-testid="not-found-page">
      <div>
        <Compass className="mx-auto h-12 w-12 text-hc-gold" />
        <p className="mt-4 font-display text-6xl font-extrabold text-hc-green">404</p>
        <h1 className="mt-2 font-display text-2xl font-bold text-hc-ink">This road doesn't go anywhere</h1>
        <p className="mt-2 text-hc-slate">The page you're looking for has moved or never existed.</p>
        <div className="mt-6 flex justify-center gap-3"><Link to="/" className="btn-primary" data-testid="not-found-home">Go home</Link><Link to="/cars" className="btn-outline">Browse cars</Link></div>
      </div>
    </div>
  );
}
